import React, { useState, useEffect } from 'react';
import { Edit2, Save, X, Calendar, DollarSign, FileText, AlertCircle } from 'lucide-react';
import supabase from '../../supabase';
import './ProjectDetailpage.css';

const ProjectDetailPage = ({ project, onProjectUpdate, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    budget: '',
    client_id: '',
    project_type: ''
  });

  // Function to calculate status based on dates
  const calculateStatus = (startDate, endDate, projectType) => {
    if (projectType === 'MileStone') {
      return 'active';
    }

    if (!startDate || !endDate) {
      return 'Not Active';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);

    if (end < today) {
      return 'completed';
    } else if (start <= today) {
      return 'active';
    } else {
      return 'Not Active';
    }
  };

  // Get current status based on form data
  const currentStatus = calculateStatus(
    formData.start_date, 
    formData.end_date, 
    formData.project_type || project?.project_type
  );

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        description: project.description || '',
        start_date: project.start_date || '',
        end_date: project.end_date || '',
        budget: project.budget || '',
        client_id: project.client_id || '',
        project_type: project.project_type || ''
      });
    }
  }, [project]);

  useEffect(() => {
    if (isEditing) {
      fetchClients();
    }
  }, [isEditing]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, client_name')
        .order('client_name');

      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      budget: project.budget || '',
      client_id: project.client_id || '',
      project_type: project.project_type || ''
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      // Validation
      if (!formData.name.trim()) {
        setError('Project name is required');
        setLoading(false);
        return;
      }

      if (formData.start_date && formData.end_date) {
        if (new Date(formData.end_date) < new Date(formData.start_date)) {
          setError('End date cannot be before start date');
          setLoading(false);
          return;
        }
      }

      // Calculate status automatically
      const calculatedStatus = calculateStatus(
        formData.start_date,
        formData.end_date,
        formData.project_type || project.project_type
      );

      // Prepare update data
      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        status: calculatedStatus, // Automatically calculated status
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        client_id: formData.client_id || null,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', project.id);

      if (updateError) throw updateError;

      setSuccess('Project updated successfully!');
      setIsEditing(false);
      
      // Call parent update function
      if (onProjectUpdate) {
        onProjectUpdate();
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating project:', err);
      setError('Failed to update project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to get status badge styling
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-badge-active';
      case 'completed':
        return 'status-badge-completed';
      case 'Not Active':
        return 'status-badge-inactive';
      default:
        return 'status-badge-inactive';
    }
  };

  // Format status for display
  const formatStatusDisplay = (status) => {
    if (status === 'Not Active') return 'Not Active';
    if (status === 'active') return 'Active';
    if (status === 'completed') return 'Completed';
    return status;
  };

  return (
    <div className="prjctdtlpg-container">
      {/* Action Bar with Close Button */}
      <div className="prjctdtlpg-action-bar">
        <h2 className="prjctdtlpg-title">Project Details</h2>
        <div className="prjctdtlpg-action-buttons">
          {!isEditing ? (
            <button 
              onClick={handleEdit} 
              className="prjctdtlpg-btn prjctdtlpg-btn-edit"
            >
              <Edit2 size={18} />
              <span>Edit</span>
            </button>
          ) : (
            <div className="prjctdtlpg-edit-actions">
              <button 
                onClick={handleCancel} 
                className="prjctdtlpg-btn prjctdtlpg-btn-cancel"
                disabled={loading}
              >
                <X size={18} />
                <span>Cancel</span>
              </button>
              <button 
                onClick={handleSave} 
                className="prjctdtlpg-btn prjctdtlpg-btn-save"
                disabled={loading}
              >
                <Save size={18} />
                <span>{loading ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
          <button 
            className="prjctdtlpg-close-button"
            onClick={onClose}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="prjctdtlpg-message prjctdtlpg-message-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="prjctdtlpg-message prjctdtlpg-message-success">
          <span>{success}</span>
        </div>
      )}

      {/* Details Grid */}
      <div className="prjctdtlpg-details-grid">
        {/* Project Name */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <FileText size={18} />
            <span>Project Name</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="prjctdtlpg-input"
              placeholder="Enter project name"
            />
          ) : (
            <div className="prjctdtlpg-value">{project.name || 'Not set'}</div>
          )}
        </div>

        {/* Status - Auto-calculated */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <span>Status {isEditing && '(Auto-calculated)'}</span>
          </label>
          {isEditing ? (
            <div>
              <div className={`prjctdtlpg-status-display ${getStatusBadgeClass(currentStatus)}`}>
                <span className="status-indicator"></span>
                {formatStatusDisplay(currentStatus)}
              </div>
              <p className="prjctdtlpg-helper-text">
                {(formData.project_type || project.project_type) === 'MileStone' 
                  ? 'Milestone projects are always active'
                  : formData.start_date && formData.end_date
                    ? 'Status is based on start and end dates'
                    : 'Enter start and end dates to see status'}
              </p>
            </div>
          ) : (
            <div className={`prjctdtlpg-status-badge ${getStatusBadgeClass(project.status)}`}>
              <span className="status-indicator"></span>
              {formatStatusDisplay(project.status)}
            </div>
          )}
        </div>

        {/* Description */}
        <div className="prjctdtlpg-field prjctdtlpg-field-full">
          <label className="prjctdtlpg-label">
            <FileText size={18} />
            <span>Description</span>
          </label>
          {isEditing ? (
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="prjctdtlpg-textarea"
              placeholder="Enter project description"
              rows="4"
            />
          ) : (
            <div className="prjctdtlpg-value">
              {project.description || 'No description provided'}
            </div>
          )}
        </div>

        {/* Client */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <span>Client</span>
          </label>
          {isEditing ? (
            <select
              name="client_id"
              value={formData.client_id}
              onChange={handleInputChange}
              className="prjctdtlpg-select"
            >
              <option value="">No client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.client_name}
                </option>
              ))}
            </select>
          ) : (
            <div className="prjctdtlpg-value">
              {project.client_name || 'No client assigned'}
            </div>
          )}
        </div>

        {/* Project Type (Read-only) */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <span>Project Type</span>
          </label>
          <div className="prjctdtlpg-value prjctdtlpg-value-readonly">
            {project.project_type || 'Not set'}
          </div>
        </div>

        {/* Start Date */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <Calendar size={18} />
            <span>Start Date</span>
          </label>
          {isEditing ? (
            <input
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              className="prjctdtlpg-input"
            />
          ) : (
            <div className="prjctdtlpg-value">{formatDate(project.start_date)}</div>
          )}
        </div>

        {/* End Date */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <Calendar size={18} />
            <span>End Date</span>
          </label>
          {isEditing ? (
            <input
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
              className="prjctdtlpg-input"
            />
          ) : (
            <div className="prjctdtlpg-value">{formatDate(project.end_date)}</div>
          )}
        </div>

        {/* Budget */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <DollarSign size={18} />
            <span>Budget</span>
          </label>
          {isEditing ? (
            <input
              type="number"
              name="budget"
              value={formData.budget}
              onChange={handleInputChange}
              className="prjctdtlpg-input"
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          ) : (
            <div className="prjctdtlpg-value">{formatCurrency(project.budget)}</div>
          )}
        </div>

        {/* Created At */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <span>Created At</span>
          </label>
          <div className="prjctdtlpg-value prjctdtlpg-value-readonly">
            {formatDate(project.created_at)}
          </div>
        </div>

        {/* Updated At */}
        <div className="prjctdtlpg-field">
          <label className="prjctdtlpg-label">
            <span>Last Updated</span>
          </label>
          <div className="prjctdtlpg-value prjctdtlpg-value-readonly">
            {formatDate(project.updated_at)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;